import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Clock, Wallet, ShieldCheck, Building2, ArrowRight, CheckCircle2 } from "lucide-react";

const FEATURES = [
  { icon: Building2, title: "Multi-Company", desc: "Run payroll for many companies and their teams from one portal." },
  { icon: Clock, title: "Time Tracker", desc: "Live clock in / clock out with automatic weekly hour totals." },
  { icon: Wallet, title: "Auto Wages", desc: "Regular + 1.5× overtime, tax estimate and net pay computed for you." },
  { icon: ShieldCheck, title: "Back-End Verification", desc: "Managers submit, admins verify & process before disbursement." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-20 backdrop-blur bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/login"><Button data-testid="header-login-btn" className="bg-sky-600 hover:bg-sky-700 rounded-full font-semibold">Sign in</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden grid-bg">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 border border-sky-100 text-sky-700 text-xs font-semibold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500 pulse-ring" /> Payroll · Attendance · Verification
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Payroll that clears
              <span className="text-sky-600"> verification </span>
              before it pays.
            </h1>
            <p className="mt-6 text-lg text-slate-500 max-w-xl">
              PayFlex Portal tracks time, computes weekly wages, captures bank & check details, and routes every payroll through back-end approval — for as many companies as you run.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/login"><Button size="lg" data-testid="hero-login-btn" className="bg-sky-600 hover:bg-sky-700 rounded-full h-12 px-7 text-base font-semibold shadow-lg shadow-sky-600/20">
                Sign in to your portal <ArrowRight className="w-4 h-4 ml-1" />
              </Button></Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {["Weekly wage engine", "Overtime at 1.5×", "Direct deposit & check"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-sky-500" /> {t}</span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="relative">
            <div className="rounded-3xl bg-slate-900 p-6 shadow-2xl shadow-slate-900/20 border border-slate-800">
              <div className="flex items-center justify-between mb-5">
                <span className="text-slate-400 text-xs font-mono uppercase tracking-widest">Weekly Payroll Run</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs font-semibold border border-amber-500/20">Pending Approval</span>
              </div>
              <div className="text-white font-mono text-4xl font-bold">$48,920.00</div>
              <div className="text-slate-500 text-sm mt-1">Net disbursement · 24 employees</div>
              <div className="mt-6 space-y-3">
                {[["Regular hours", "912.0 h"], ["Overtime (1.5×)", "38.5 h"], ["Tax estimate", "$12,230.00"]].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">{k}</span>
                    <span className="text-slate-200 font-mono font-semibold">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <div className="flex-1 rounded-xl bg-sky-500 text-white text-center py-2.5 text-sm font-semibold">Submit for approval</div>
                <div className="rounded-xl bg-slate-800 text-slate-300 px-4 py-2.5 text-sm font-semibold">Preview</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-slate-100 bg-white p-6 hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-lg">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-sm text-slate-400">
          <Logo size="sm" />
          <span>© 2026 PayFlex Portal</span>
        </div>
      </footer>
    </div>
  );
}
